import Grid from '@material-ui/core/Grid/Grid';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { AppState } from '../../../../store';
import { signSetAction } from '../../../../store/signup/actions';
import {
    CLICK_ORG_SEARCH_SEARCH_BTN,
    CLICK_ORG_SEARCH_CITY_INPUT,
    CLICK_ORG_SEARCH_NAME_INPUT,
} from '../../../../utils/clevertapEvents';
import { pushEvent } from '../../../../utils/clevertapUtils';
import { maxSearchResultWidth } from '../InfoRow';
import LocationAutoComplete, {
    isLocationValid,
} from '../../components/autocomplete/LocationAutoComplete';
import SearchTextField from '../../components/SearchTextField';
import { noValidation } from '../../components/SimpleTextFieldComponent';
import SearchIcon from '../../../../assets/search-button-icon.svg';
import { LocationSelected } from '../../../../store/signup/types';
import { styled } from '@mui/material';

const OrganizationSearchGrid = styled('div')(({ theme }) => ({
    maxWidth: maxSearchResultWidth,
    margin: '0 auto',
    padding: theme.spacing(1),
    backgroundColor: '#F6F6F6',
    display: 'flex',
    alignItems: 'center',
    '& .MuiInputBase-root': {
        backgroundColor: '#fff',
    },
    '& .left': {
        paddingRight: theme.spacing(1),
    },
    '& .right': {
        paddingLeft: theme.spacing(1),
    },
    '& .button button:disabled': {
        backgroundColor: '#ccc',
    },
    '& .button': {
        width: 56,
    },
    '& .button button': {
        cursor: 'pointer',
        height: 58,
        width: '100%',
        border: 'none',
        boxShadow: '0px 0px 6px rgba(103, 110, 119, 0.3)',
        borderRadius: '6px',
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
    },
    '& .text-fields': {
        flex: '1 1 auto',
        paddingRight: theme.spacing(2),
    },
    [theme.breakpoints.down('xs')]: {
        '& .name-input input': {
            paddingTop: 12,
            paddingBottom: 12,
        },
        '& .location-input .MuiInputBase-root': {
            paddingTop: 2,
            paddingBottom: 2,
        },
        '& .text-fields': {
            paddingRight: 5,
        },
        '& .left': {
            paddingRight: 3,
        },
        '& .right': {
            paddingLeft: 3,
        },
        '& .button button': {
            height: 45,
        },
        '& .button button, & .button': {
            width: 40,
        },
        // stupid Safari bottom navigation covers up bottom card
        '_::-webkit-full-page-media, _:future, :root .safari_only': {
            paddingBottom: 65,
        },
    },
}));

const OrganizationSearchField: React.FC<{ readOnly?: boolean }> = ({
    readOnly,
}) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { defaultValueOrg, defaultValueLoc } = useSelector(
        (state: AppState) => ({
            defaultValueOrg: state.Signup.organizationName,
            defaultValueLoc: state.Signup.organizationLocation,
        }),
    );
    const placeholder = useMemo(
        () => t('signup.organizationName.placeholders.search'),
        [t],
    );
    const [organizationName, setOrganizationName] =
        useState<string>(defaultValueOrg);
    const [location, setLocation] = useState<LocationSelected>(defaultValueLoc);
    const onChangeName = (value: string) => {
        setOrganizationName(value);
    };
    useEffect(() => {
        if (location !== defaultValueLoc) setLocation(defaultValueLoc);
        if (organizationName !== defaultValueOrg)
            setOrganizationName(defaultValueOrg);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValueLoc, defaultValueOrg]);
    const onClick = () => {
        pushEvent(CLICK_ORG_SEARCH_SEARCH_BTN);
        dispatch(
            signSetAction({ organizationName, organizationLocation: location }),
        );
    };
    return (
        <OrganizationSearchGrid>
            <Grid container className="text-fields">
                <Grid item xs={6} sm={6} md={6} lg={6} xl={6} className="left">
                    <SearchTextField
                        value={organizationName}
                        isFocus={true}
                        type="text"
                        placeholder={placeholder}
                        onChange={onChangeName}
                        validation={noValidation}
                        maxLength={255}
                        readOnly={readOnly}
                        className="name-input"
                        onClick={() => pushEvent(CLICK_ORG_SEARCH_NAME_INPUT)}
                    />
                </Grid>
                <Grid item xs={6} sm={6} md={6} lg={6} xl={6} className="right">
                    <LocationAutoComplete
                        readOnly={readOnly}
                        location={location}
                        onSubmit={setLocation}
                        className="location-input"
                        onClick={() => pushEvent(CLICK_ORG_SEARCH_CITY_INPUT)}
                    />
                </Grid>
            </Grid>
            <div className="button">
                <button
                    onClick={onClick}
                    disabled={
                        organizationName.length === 0 ||
                        !isLocationValid(location)
                    }
                >
                    <img src={SearchIcon} alt="Search" title="Search" />
                </button>
            </div>
        </OrganizationSearchGrid>
    );
};

export default OrganizationSearchField;
